import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar, Clock, User, CheckCircle, X, ChevronLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { toast } from 'sonner';

export default function ProviderAppointments() {
  const { api } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchDoctors = useCallback(async () => {
    try {
      const response = await api.get('/provider/doctors');
      setDoctors(response.data);
    } catch (error) {
    }
  }, [api]);

  const fetchAppointments = useCallback(async () => {
    try {
      let url = '/provider/appointments';
      const params = [];
      if (selectedDoctor && selectedDoctor !== 'all') params.push(`doctor_id=${selectedDoctor}`);
      if (statusFilter && statusFilter !== 'all') params.push(`status=${statusFilter}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await api.get(url);
      setAppointments(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [api, selectedDoctor, statusFilter]);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const handleUpdateStatus = async (appointmentId, status) => {
    try {
      await api.put(`/provider/appointments/${appointmentId}/status?status=${status}`);
      toast.success(`Status atualizado para ${status}`);
      fetchAppointments();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pendente', className: 'bg-amber-100 text-amber-700' },
      confirmed: { label: 'Confirmada', className: 'bg-emerald-100 text-emerald-700' },
      completed: { label: 'Realizada', className: 'bg-slate-100 text-slate-700' },
      cancelled: { label: 'Cancelada', className: 'bg-red-100 text-red-700' },
      no_show: { label: 'Não compareceu', className: 'bg-red-100 text-red-700' },
    };
    const { label, className } = statusMap[status] || statusMap.pending;
    return <Badge className={className}>{label}</Badge>;
  };

  const AppointmentCard = ({ appointment }) => (
    <div className="bg-white rounded-2xl border border-slate-100 p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center">
            <User className="w-7 h-7 text-slate-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              {getStatusBadge(appointment.status)}
              {appointment.payment_type === 'health_plan' ? (
                <Badge variant="secondary">Convênio</Badge>
              ) : (
                <Badge variant="secondary">Particular</Badge>
              )}
            </div>
            <h3 className="font-semibold text-slate-900">{appointment.doctor_name}</h3>
            <p className="text-sm text-slate-600">{appointment.specialty_name}</p>
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {format(new Date(appointment.date), 'dd/MM/yyyy')}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {appointment.time_slot}
              </span>
            </div>
          </div>
        </div>
      </div>

      {['pending', 'confirmed'].includes(appointment.status) && (
        <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
          {appointment.status === 'pending' && (
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => handleUpdateStatus(appointment.id, 'confirmed')}
              data-testid={`confirm-btn-${appointment.id}`}
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Confirmar
            </Button>
          )}
          {appointment.status === 'confirmed' && (
            <Button
              size="sm"
              className="bg-primary"
              onClick={() => handleUpdateStatus(appointment.id, 'completed')}
              data-testid={`complete-btn-${appointment.id}`}
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Concluir
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="text-red-600"
            onClick={() => handleUpdateStatus(appointment.id, 'cancelled')}
            data-testid={`cancel-btn-${appointment.id}`}
          >
            <X className="w-4 h-4 mr-1" />
            Cancelar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleUpdateStatus(appointment.id, 'no_show')}
          >
            Não compareceu
          </Button>
        </div>
      )}
    </div>
  );

  const pendingAppointments = appointments.filter(a => a.status === 'pending');
  const confirmedAppointments = appointments.filter(a => a.status === 'confirmed');
  const completedAppointments = appointments.filter(a => ['completed', 'cancelled', 'no_show'].includes(a.status));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="provider-appointments">
      <Navbar />

      <div className="container-app py-8">
        <Link to="/prestador" className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-6">
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </Link>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Consultas</h1>
            <p className="text-slate-600 mt-1">Gerencie os agendamentos da clínica</p>
          </div>
          <div className="flex gap-3">
            <Select value={selectedDoctor} onValueChange={setSelectedDoctor}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Todos médicos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos médicos</SelectItem>
                {doctors.map((doctor) => (
                  <SelectItem key={doctor.id} value={doctor.id}>
                    {doctor.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Tabs defaultValue="pending">
          <TabsList className="mb-6">
            <TabsTrigger value="pending">
              Pendentes ({pendingAppointments.length})
            </TabsTrigger>
            <TabsTrigger value="confirmed">
              Confirmadas ({confirmedAppointments.length})
            </TabsTrigger>
            <TabsTrigger value="completed">
              Histórico ({completedAppointments.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse h-32 bg-white rounded-2xl"></div>
                ))}
              </div>
            ) : pendingAppointments.length > 0 ? (
              <div className="space-y-4">
                {pendingAppointments.map((appointment) => (
                  <AppointmentCard key={appointment.id} appointment={appointment} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
                <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Sem consultas pendentes</h3>
                <p className="text-slate-600">Novas consultas aparecerão aqui</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="confirmed">
            {confirmedAppointments.length > 0 ? (
              <div className="space-y-4">
                {confirmedAppointments.map((appointment) => (
                  <AppointmentCard key={appointment.id} appointment={appointment} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
                <CheckCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Sem consultas confirmadas</h3>
                <p className="text-slate-600">Confirme as consultas pendentes</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="completed">
            {completedAppointments.length > 0 ? (
              <div className="space-y-4">
                {completedAppointments.map((appointment) => (
                  <AppointmentCard key={appointment.id} appointment={appointment} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
                <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Sem histórico</h3>
                <p className="text-slate-600">O histórico de consultas aparecerá aqui</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
}
